import { FC } from "react";
import { useTranslation } from "react-i18next";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { MRT_SortingState } from "material-react-table";
import { api } from "@/helper/api";
import { useConfirmationModal } from "@/context/confirmationModalContext";

interface CsvExportButtonProps {
  entity: string | null;
  additionalFilter?: {
    id: string;
    value: string;
    customFilterParameter?: string;
  }[];
  columnOrdering?: string[];
  additionalSorting: MRT_SortingState;
  handleError: (error: any) => void;
  subdomain?: string;
}

const CsvExportButton: FC<CsvExportButtonProps> = ({
  entity,
  additionalFilter,
  additionalSorting,
  handleError,
  columnOrdering,
}) => {
  const { t } = useTranslation();
  const { showConfirmationModal } = useConfirmationModal();

  const handleCsvExport = async () => {
    if (!entity) return;

    const additionalUrlParameter: any = {};

    //  entity-specific first param
    if (entity === "trackedService") {
      additionalUrlParameter["export_service_record"] = true;
    }

    if (additionalFilter && additionalFilter.length > 0) {
      additionalFilter.forEach(({ id, value, customFilterParameter }) => {
        if (id && value) {
          const key = `${id}${customFilterParameter}`;
          additionalUrlParameter[key] = value;
        }
      });
    }

    if (additionalSorting.length > 0) {
      const ordering = additionalSorting
        .map(({ id, desc }) => (desc ? `-${id}` : id))
        .join(",");
      additionalUrlParameter["ordering"] = ordering;
    }

    if (
      entity === "trackedService" &&
      columnOrdering &&
      columnOrdering?.length !== 0
    ) {
      const columnMapping = getMappedColumns(
        columnOrdering,
        TRACKEDSERVICE_COLUMN_FIELD_MAP
      );
      additionalUrlParameter["columns"] = columnMapping;
    }

    try {
      const response: any = await new Promise((resolve, reject) => {
        api.genericApiRequest({
          method: "get",
          entity: entity,
          parametersToRender: additionalUrlParameter,

          successHandler: (res: any) => {
            resolve(res);
          },
          failHandler: (error: any) => {
            reject(error);
          },
        });
      });

      if (response.data.status === "service record export queued") {
        showConfirmationModal(t("trackedservice_csv_export_queued_info"), {
          disableCancelButton: true,
          title: t("request_received"),
        });
      }
    } catch (error) {
      handleError(error);
    }
  };

  return (
    <GenericButton onClick={handleCsvExport}>{t("csv_export")}</GenericButton>
  );
};

export default CsvExportButton;

const getMappedColumns = (
  shortCols: string[],
  mapping: Record<string, string>
): string[] => {
  return shortCols.map((key) => mapping[key] || key);
};

const TRACKEDSERVICE_COLUMN_FIELD_MAP: Record<string, string> = {
  checked: "checked",
  billed: "billed",
  customer_id: "company_subject_customer_id",
  company_subject: "company_subject_name",
  parent_company: "parent_company_name",
  subject: "person_subject_name",
  building: "building_name",
  description: "description",
  category: "category_name",
  examination: "examination_name",
  executed_on: "executed_on",
  status: "appointment_status",
  executed_by: "executed_by_name",
  assisted_by: "assisted_by_name",
  month: "month",
  estimated_duration: "estimated_duration",
  actual_duration: "actual_duration",
  price: "price",
  vat: "vat",
  tracked_service_type: "tracked_service_type",
  info_ops: "info_ops",
  info_finance: "info_finance",
  checked_on: "checked_on",
  checked_by: "checked_by_name",
  billed_on: "billed_on",
  billed_by: "billed_by_name",
  created_on: "created_on",
  tracked_service_template: "tracked_service_template",
  on_site: "on_site",
  stundensatz_arbeitssicherheit_gb: "stundensatz_arbeitssicherheit_gb",
  stundensatz_betriebsmedizin_gb: "stundensatz_betriebsmedizin_gb",
  stundensatz_arbeitssicherheit_bplus: "stundensatz_arbeitssicherheit_bplus",
  stundensatz_betriebsmedizin_bplus: "stundensatz_betriebsmedizin_bplus",
  reisekosten_uebernachtung_uen: "reisekosten_uebernachtung_uen",
  reisekosten_reisezeit: "reisekosten_reisezeit",
  reisekosten_pauschale: "reisekosten_pauschale",
};
